import { createContext, useContext } from 'react';

import type { AugmentationStatus } from '../../../modules/properties/services/schema';

export type AugmentationViewMode = 'original' | 'augmented';

export interface AugmentationViewState {
  sourcePageId: string;
  renderedPageId: string;
  augmentedPageId: string | null;
  mode: AugmentationViewMode;
  sharePageId: string;
}

const AugmentationViewContext = createContext<AugmentationViewState | null>(
  null
);

export const AugmentationViewProvider = AugmentationViewContext.Provider;

export function useAugmentationView(): AugmentationViewState | null {
  return useContext(AugmentationViewContext);
}

export function useSourcePageId(fallbackPageId?: string): string | undefined {
  return useAugmentationView()?.sourcePageId ?? fallbackPageId;
}

export function canRenderAugmentedPage({
  shouldShowAugmented,
  hasExistingAugmentedPage,
  isSharedMode,
  augmentedDocReady,
}: {
  shouldShowAugmented: boolean;
  hasExistingAugmentedPage: boolean;
  isSharedMode: boolean;
  augmentedDocReady: boolean;
}): boolean {
  return (
    shouldShowAugmented &&
    (hasExistingAugmentedPage || isSharedMode || augmentedDocReady)
  );
}

export function resolveAugmentationViewState({
  sourcePageId,
  augmentationStatus,
  augmentedPageId,
  augmentedReady,
  forceRenderSource = false,
  augmentedUnavailable = false,
}: {
  sourcePageId: string;
  augmentationStatus: AugmentationStatus;
  augmentedPageId: string | null;
  augmentedReady: boolean;
  forceRenderSource?: boolean;
  augmentedUnavailable?: boolean;
}): AugmentationViewState {
  const hasAugmentedView =
    augmentationStatus === 'Augmented' &&
    augmentedPageId !== null &&
    !augmentedUnavailable;
  const shouldRenderAugmented =
    hasAugmentedView && augmentedReady && !forceRenderSource;
  const renderedPageId = shouldRenderAugmented ? augmentedPageId : sourcePageId;
  const mode = hasAugmentedView ? 'augmented' : 'original';
  return {
    sourcePageId,
    renderedPageId,
    augmentedPageId,
    mode,
    sharePageId:
      hasAugmentedView && augmentedPageId ? augmentedPageId : renderedPageId,
  };
}
